import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, Mail, Shield, Key, Save, Edit } from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '../components/ui/PageHeader';
import { Card } from '../components/ui/Card';
import Button from '../components/ui/Button';

const Profile: React.FC = () => {
  const { user, changePassword, updateProfile, isLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');

  // Stati per aggiornamento profilo
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    avatar_url: user?.avatar_url || ''
  });

  // Stati per cambio password
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const success = await updateProfile(profileData);
      if (success) {
        toast.success('Profilo aggiornato con successo');
      } else {
        toast.error('Errore nell\'aggiornamento del profilo');
      }
    } catch (error) {
      toast.error('Errore nell\'aggiornamento del profilo');
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Le password non corrispondono');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }

    try {
      const success = await changePassword(passwordData.currentPassword, passwordData.newPassword);
      if (success) {
        toast.success('Password cambiata con successo');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        toast.error('Errore nel cambio password');
      }
    } catch (error) {
      toast.error('Errore nel cambio password');
    }
  };

  const getRoleLabel = (role: string) => {
    const roleMap = {
      admin: 'Amministratore',
      videomaker: 'Video Maker',
      video_editor: 'Video Editor',
      copywriter: 'Copywriter',
      publisher: 'Publisher'
    };
    return roleMap[role as keyof typeof roleMap] || role;
  };

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Header */}
      <PageHeader
        title="Il Mio Profilo"
        subtitle="Gestisci le informazioni del tuo account"
      />

      {/* Profile Card */}
      <Card>
        <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-3 sm:space-y-0 sm:space-x-4 text-center sm:text-left">
          <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-gradient-to-br from-[#fff7a8] to-[#fdf751] flex items-center justify-center p-0.5 flex-shrink-0">
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt="Avatar"
                className="h-full w-full rounded-full object-cover"
              />
            ) : (
              <div className="h-full w-full rounded-full bg-white flex items-center justify-center">
                <span className="text-xl sm:text-2xl font-medium text-slate-900">
                  {user?.first_name ? user.first_name[0] : user?.username[0] || 'U'}
                </span>
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl font-semibold text-slate-900 truncate">
              {user?.first_name && user?.last_name
                ? `${user.first_name} ${user.last_name}`
                : user?.username}
            </h2>
            <p className="text-sm sm:text-base text-slate-500 truncate">@{user?.username}</p>
            <div className="flex items-center justify-center sm:justify-start gap-2 mt-1">
              <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-700" />
              <span className="text-xs sm:text-sm text-slate-500">
                {/* Publisher è un ruolo aggiuntivo: si affianca a quello principale */}
                {user?.is_publisher && user.role !== 'publisher'
                  ? `${getRoleLabel(user?.role || '')} · Publisher`
                  : getRoleLabel(user?.role || '')}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <Card>
        <div className="border-b border-slate-200">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 px-3 sm:px-6 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center ${
                activeTab === 'profile'
                  ? 'border-[#fdf751] text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <User className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
              <span className="hidden xs:inline">Informazioni</span> Profilo
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center ${
                activeTab === 'password'
                  ? 'border-[#fdf751] text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <Key className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
              Sicurezza
            </button>
          </nav>
        </div>

        <div className="p-3 sm:p-4 md:p-6">
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileUpdate} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">
                    Nome
                  </label>
                  <input
                    type="text"
                    value={profileData.first_name}
                    onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                    className="block w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20"
                    placeholder="Il tuo nome"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">
                    Cognome
                  </label>
                  <input
                    type="text"
                    value={profileData.last_name}
                    onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                    className="block w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20"
                    placeholder="Il tuo cognome"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">
                  URL Avatar
                </label>
                <input
                  type="url"
                  value={profileData.avatar_url}
                  onChange={(e) => setProfileData({ ...profileData, avatar_url: e.target.value })}
                  className="block w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20"
                  placeholder="https://esempio.com/avatar.jpg"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    value={user?.username || ''}
                    disabled
                    className="block w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-slate-200 rounded-xl shadow-sm bg-slate-50 text-slate-500"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">
                    Email
                  </label>
                  <div className="flex rounded-xl shadow-sm">
                    <div className="relative flex items-stretch flex-grow">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-400" />
                      </div>
                      <input
                        type="email"
                        value={user?.email || ''}
                        disabled
                        className="block w-full pl-9 sm:pl-10 px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-slate-200 rounded-xl shadow-sm bg-slate-50 text-slate-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  disabled={isLoading}
                  variant="primary"
                  className="w-full sm:w-auto min-h-[44px] text-sm sm:text-base"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isLoading ? 'Salvando...' : 'Salva Modifiche'}
                </Button>
              </div>
            </form>
          )}

          {activeTab === 'password' && (
            <form onSubmit={handlePasswordChange} className="space-y-4 sm:space-y-6 max-w-md">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">
                  Password Attuale
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="block w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20"
                  required
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">
                  Nuova Password
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="block w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20"
                  minLength={6}
                  required
                />
                <p className="mt-1 text-xs sm:text-sm text-slate-500">
                  La password deve essere di almeno 6 caratteri
                </p>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">
                  Conferma Nuova Password
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="block w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20"
                  required
                />
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 sm:py-2 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 min-h-[44px]"
                >
                  <Key className="h-4 w-4 mr-2" />
                  {isLoading ? 'Cambiando...' : 'Cambia Password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Profile;
